import { useEffect } from 'react';

/**
 * Signals the boot splash in index.html to fade out. Fires once per session,
 * when the first real layout is mounted AND painted. Double rAF + small hold
 * ensures the browser has actually rendered the content beneath the splash
 * before we fade it.
 *
 * `ready` — это «человеку есть что видеть», а не «компонент смонтировался».
 * Разница стоила первому запуску четырёх секунд пустой карточки: `PublicLayout`
 * снимал сплеш сразу, а под ним в это время поднимался Clerk. Поэтому маршрут,
 * который знает про своё ожидание сам (см. LinkPortalScreen), объявляет
 * готовность за себя, а лэйаут за него сигнал не шлёт.
 */
export function useAppReady(ready: boolean = true) {
  useEffect(() => {
    if (!ready) return;

    // Очистка отменяет ВСЮ цепочку, а не только её начало. Прежде внутренний
    // кадр и таймер не отменялись — их держал флаг `cancelled`: очистка,
    // случившаяся между кадрами, отменить уже назначенный внутренний кадр не
    // могла, и он вместе с таймером доживал до конца, чтобы прочитать флаг и
    // ничего не сделать. Отменять нечего — нечему и срабатывать.
    let inner = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        timer = setTimeout(() => window.dispatchEvent(new Event('app:ready')), 120);
      });
    });

    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
      if (timer !== undefined) clearTimeout(timer);
    };
  }, [ready]);
}
